import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Box,
    Typography,
    Chip,
    Divider,
    CircularProgress,
    Alert,
    IconButton,
    Skeleton,
    Avatar,
    Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import CommentIcon from '@mui/icons-material/Comment';
import { StaffService } from '../api/services/staffService';

function StatusChip({ status }) {
    const colorMap = { Approved: 'success', Rejected: 'error', Pending: 'warning' };
    return <Chip label={status || 'Pending'} size="small" color={colorMap[status] || 'default'} />;
}

function DetailRow({ label, value }) {
    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.3, fontWeight: 500 }}>
                {value || '—'}
            </Typography>
        </Box>
    );
}

function SkeletonDetail() {
    return (
        <Box sx={{ p: 3 }}>
            <Skeleton width="60%" height={32} sx={{ mb: 1 }} />
            <Skeleton width="40%" height={20} sx={{ mb: 3 }} />
            <Divider sx={{ mb: 3 }} />
            {[...Array(6)].map((_, i) => (
                <Box key={i} sx={{ mb: 2.5 }}>
                    <Skeleton width="30%" height={14} sx={{ mb: 0.5 }} />
                    <Skeleton width="70%" height={20} />
                </Box>
            ))}
        </Box>
    );
}

export default function LeaveDetailDrawer({ open, onClose, leaveId }) {
    const [leaveDetail, setLeaveDetail] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open || !leaveId) return;

        const fetchDetail = async () => {
            setIsLoading(true);
            setError('');
            setLeaveDetail(null);
            try {
                const data = await StaffService.getLeaveRequestById(leaveId);
                setLeaveDetail(data);
            } catch (err) {
                const status = err.response?.status;
                if (status === 404) {
                    setError('Leave request not found. It may have been deleted.');
                } else {
                    setError(err.response?.data?.message || err.message || 'Failed to load leave request details.');
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchDetail();
    }, [open, leaveId]);

    const formatDateTime = (isoStr) => {
        if (!isoStr) return '—';
        return new Date(isoStr).toLocaleString([], {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString([], { year: 'numeric', month: 'long', day: 'numeric' });
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" fontWeight="bold">Leave Request Details</Typography>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </Box>

            {isLoading && <SkeletonDetail />}

            {!isLoading && error && (
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            )}

            {!isLoading && !error && leaveDetail && (
                <Box sx={{ p: 3, overflowY: 'auto' }}>
                    {/* Status banner */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <Avatar sx={{ bgcolor: 'primary.light', width: 48, height: 48 }}>
                            <PersonIcon />
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                                {leaveDetail.staff?.fullName || leaveDetail.staffName || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {leaveDetail.staff?.employeeId || ''}
                            </Typography>
                        </Box>
                        <Box sx={{ ml: 'auto' }}>
                            <StatusChip status={leaveDetail.status} />
                        </Box>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* Employee Details */}
                    <Typography variant="overline" color="primary" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                        <PersonIcon fontSize="small" /> Employee Details
                    </Typography>
                    <Grid container spacing={1} sx={{ mb: 3 }}>
                        <Grid item xs={6}>
                            <DetailRow label="Department" value={leaveDetail.staff?.department?.name || leaveDetail.department || '—'} />
                        </Grid>
                        <Grid item xs={6}>
                            <DetailRow label="Designation" value={leaveDetail.staff?.designation || leaveDetail.designation || '—'} />
                        </Grid>
                    </Grid>

                    <Divider sx={{ mb: 3 }} />

                    {/* Leave Details */}
                    <Typography variant="overline" color="primary" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                        <CalendarMonthIcon fontSize="small" /> Leave Details
                    </Typography>
                    <Grid container spacing={1} sx={{ mb: 1 }}>
                        <Grid item xs={6}>
                            <DetailRow label="Leave Type" value={leaveDetail.leaveType || leaveDetail.type} />
                        </Grid>
                        <Grid item xs={6}>
                            <DetailRow label="Total Days" value={leaveDetail.totalDays != null ? `${leaveDetail.totalDays} day(s)` : '—'} />
                        </Grid>
                        <Grid item xs={6}>
                            <DetailRow label="Start Date" value={formatDate(leaveDetail.startDate)} />
                        </Grid>
                        <Grid item xs={6}>
                            <DetailRow label="End Date" value={formatDate(leaveDetail.endDate)} />
                        </Grid>
                    </Grid>
                    <DetailRow label="Reason" value={leaveDetail.reason} />

                    <Divider sx={{ mb: 3 }} />

                    {/* Status & Audit */}
                    <Typography variant="overline" color="primary" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                        <AccessTimeIcon fontSize="small" /> Status & Audit
                    </Typography>
                    <DetailRow label="Submitted On" value={formatDateTime(leaveDetail.createdAt)} />
                    <DetailRow label="Last Updated" value={formatDateTime(leaveDetail.updatedAt)} />
                    {leaveDetail.reviewedBy && (
                        <DetailRow label="Reviewed By" value={leaveDetail.reviewedBy?.fullName || leaveDetail.reviewedBy} />
                    )}

                    {leaveDetail.comments && (
                        <>
                            <Divider sx={{ mb: 3 }} />
                            <Typography variant="overline" color="primary" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                                <CommentIcon fontSize="small" /> Manager Comments
                            </Typography>
                            <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2 }}>
                                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                    "{leaveDetail.comments}"
                                </Typography>
                            </Box>
                        </>
                    )}
                </Box>
            )}
        </Drawer>
    );
}
